import React from "react";
import { Autosuggest } from "@amzn/awsui-components-react";
import { Stage } from "../../lusca-search-api/generated-src";
import { useInvestigationAssignees } from "../../hooks/useInvestigations";

/**
 * Who may own an investigation, as the pickers see it: the Bindle-lock member list from the
 * API, or "let the backend decide" while that list is unavailable.
 */
export interface AssigneeDirectory {
  knownAliases: string[];
  isLoading: boolean;
  /** The directory request failed; aliases are checked on submit instead. */
  unavailable: boolean;
  /** Whether `alias` (any case / whitespace) may own a case, as far as the client can tell. */
  isKnown: (alias: string) => boolean;
}

/** Normalise a typed alias the way the backend does. */
export const normaliseAlias = (alias: string) => alias.trim().toLowerCase();

/**
 * Loads the assignee directory while `enabled` (a picker is open) and caches it for the
 * session. While the directory is down every alias is provisionally known so the backend's
 * validation is the final word.
 */
export function useAssigneeDirectory(
  stage: Stage,
  enabled: boolean
): AssigneeDirectory {
  const assignees = useInvestigationAssignees(stage, enabled);
  const knownAliases = assignees.data?.aliases ?? [];
  const unavailable = !!assignees.error;
  return {
    knownAliases,
    isLoading: assignees.isLoading,
    unavailable,
    isKnown: (alias) =>
      unavailable || knownAliases.includes(normaliseAlias(alias)),
  };
}

export interface PinnedAssignee {
  alias: string;
  /** Shown beside the alias, e.g. "current owner". */
  description?: string;
}

interface AssigneeAutosuggestProps {
  directory: AssigneeDirectory;
  value: string;
  onChange: (value: string) => void;
  /** Aliases surfaced at the top of the list (participants of the case), in order. */
  pinned?: PinnedAssignee[];
  ariaLabel?: string;
  placeholder?: string;
}

/**
 * Alias picker over the Lusca Search user list. The typed text is deliberately kept selectable
 * so an exact match is always one Enter away; the row for it says whether it is a known user.
 */
export const AssigneeAutosuggest: React.FC<AssigneeAutosuggestProps> = ({
  directory,
  value,
  onChange,
  pinned = [],
  ariaLabel = "New owner alias",
  placeholder = "Start typing an alias",
}) => {
  const pinnedAliases = new Set(pinned.map((p) => p.alias));
  const options = [
    ...pinned.map((p) => ({
      value: p.alias,
      label: p.alias,
      description: p.description,
    })),
    ...directory.knownAliases
      .filter((alias) => !pinnedAliases.has(alias))
      .map((alias) => ({ value: alias, label: alias })),
  ];

  // Cloudscape always shows a row for the typed text; describe that text, not the list.
  const enteredTextLabel = (text: string) => {
    const typed = normaliseAlias(text);
    if (directory.isLoading) return `Checking "${typed}"…`;
    return directory.isKnown(typed)
      ? `Use alias "${typed}"`
      : `No Lusca Search user named "${typed}"`;
  };

  return (
    <Autosuggest
      ariaLabel={ariaLabel}
      placeholder={placeholder}
      value={value}
      options={options}
      statusType={directory.isLoading ? "loading" : "finished"}
      loadingText="Loading Lusca Search users"
      filteringType="auto"
      onChange={({ detail }) => onChange(detail.value)}
      onSelect={({ detail }) =>
        onChange(detail.selectedOption?.value ?? detail.value)
      }
      enteredTextLabel={enteredTextLabel}
      empty="No Lusca Search users found"
    />
  );
};

export default AssigneeAutosuggest;
